import React, {useCallback, useEffect, useState} from 'react';
import Card from "@mui/material/Card";
import SoftBox from "../../../components/SoftBox";
import SoftTypography from "../../../components/SoftTypography";
import SoftButton from "../../../components/SoftButton";
import SoftAvatar from "../../../components/Avatar";
import PropTypes from "prop-types";
import {v4 as uuidv4} from "uuid";
import Grid from "@mui/material/Grid";
import {Add, Delete, Visibility} from '@mui/icons-material';
import childApiClient from '../../../api/children/childrenApiClient';
import ChildManagementDialog from './ChildManagementDialog';
import {useNavigate} from 'react-router-dom';
import DeleteConfirmDialog from '../../../components/Dialogs/DeleteConfirmDialog';

function GuardianDetail({guardian}) {
    const [guardianChildren, setGuardianChildren] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [childManagementOpen, setChildManagementOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [childToDelete, setChildToDelete] = useState(null);
    const navigate = useNavigate();

    const loadGuardianChildren = useCallback(async () => {
        if (!guardian?.id) return;

        try {
            setLoading(true);
            console.log('Loading children for guardian ID:', guardian.id);

            // Check if user is authenticated
            const token = localStorage.getItem('token');
            console.log('Auth token exists:', !!token);
            if (token) {
                console.log('Token length:', token.length);
            }

            const response = await childApiClient.getAllChildrenByGuardianId(guardian.id);
            console.log('Guardian children response:', response);
            console.log('Response type:', typeof response);
            console.log('Response is array:', Array.isArray(response));
            if (response) {
                console.log('Response keys:', Object.keys(response));
            }
            let childrenData = response.data;
            setGuardianChildren(childrenData);
        } catch (error) {
            console.error('Error loading guardian children:', error);
            console.error('Error response:', error.response);
            console.error('Error status:', error.response?.status);
            console.error('Error data:', error.response?.data);

            if (error.response?.status === 403) {
                setError('Access denied. Please check your authentication.');
            } else {
                setError('Failed to load children: ' + (error.message || 'Unknown error'));
            }
            setGuardianChildren([]);
        } finally {
            setLoading(false);
        }
    }, [guardian?.id]);

    useEffect(() => {
        if (guardian?.id) {
            loadGuardianChildren();
        }
    }, [guardian?.id, loadGuardianChildren]);

    const handleChildManagementUpdate = () => {
        loadGuardianChildren();
    };

    const handleDeleteChild = (child) => {
        setChildToDelete(child);
        setDeleteConfirmOpen(true);
    };

    const handleConfirmDeleteChild = async () => {
        if (!childToDelete) return;

        try {
            await childApiClient.removeGuardianFromChild(childToDelete.childId, guardian.id);
            await loadGuardianChildren();
            setDeleteConfirmOpen(false);
            setChildToDelete(null);
        } catch (error) {
            console.error('Error deleting child:', error);
            setError('Failed to delete child');
        }
    };

    const handleCancelDeleteChild = () => {
        setDeleteConfirmOpen(false);
        setChildToDelete(null);
    };

    const labels = [];
    const values = [];
    Object.keys(guardian).forEach((el) => {
        const notShow = ["profilePic", "id", "createdAt", "updatedAt", "username", "images", "active", "relationship"];
        if (el.match(/[A-Z\s]+/) && !notShow.includes(el)) {
            const uppercaseLetter = Array.from(el).find((i) => i.match(/[A-Z]+/));
            const newElement = el.replace(uppercaseLetter, ` ${uppercaseLetter.toLowerCase()}`);
            labels.push(newElement);
            values.push(guardian[el]);
        } else if (!notShow.includes(el)) {
            labels.push(el);
            values.push(guardian[el]);
        }
    });

    const renderItems = labels.map((label, key) => (
        <SoftBox key={label} display="flex" py={1} pr={2}>
            <SoftTypography variant="button" fontWeight="bold" textTransform="capitalize">
                {label}: &nbsp;
            </SoftTypography>
            <SoftTypography variant="button" fontWeight="regular" color="text">
                &nbsp;{values[key]}
            </SoftTypography>
        </SoftBox>
    ));

    const renderGuardianInfo = () => (
        <Card sx={{height: "100%"}}>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <SoftBox pt={2} px={2}>
                        <SoftTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                            {guardian.firstName} {guardian.lastName}
                        </SoftTypography>
                        <SoftBox
                            key={uuidv4()}
                            component="td"
                            p={1}
                            textAlign="center"
                        >
                            <img
                                src={`data:image/png;base64,${guardian.profilePic}`}
                                alt="profile-image"
                                style={{width: "100%", height: "100%"}}
                            />
                        </SoftBox>
                    </SoftBox>
                </Grid>
                <Grid item xs={6}>
                    <SoftBox p={2}>
                        <SoftBox>
                            {renderItems}
                        </SoftBox>
                    </SoftBox>
                </Grid>
            </Grid>
        </Card>
    );


    console.log('GuardianDetail rendering with children count:', guardianChildren.length);

    return (
        <SoftBox>
            <Grid container spacing={3}>
                {/* Guardian Information */}
                <Grid item xs={12} md={6} xl={6}>
                    {renderGuardianInfo()}
                </Grid>

                {/* Children Information */}
                <Grid item xs={12} md={6} xl={6}>
                    <Card sx={{height: "100%"}}>
                        <SoftBox display="flex" justifyContent="space-between" alignItems="center" pt={2} px={2}>
                            <SoftTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                                Children ({guardianChildren.length})
                            </SoftTypography>
                            <SoftButton
                                variant="outlined"
                                color="text"
                                size="small"
                                onClick={() => setChildManagementOpen(true)}
                                startIcon={<Add/>}
                            >
                                Manage Children
                            </SoftButton>
                        </SoftBox>
                        <SoftBox p={2}>
                            {loading ? (
                                <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight="100px">
                                    <SoftTypography variant="body2" color="text.secondary">
                                        Loading children...
                                    </SoftTypography>
                                </SoftBox>
                            ) : (Array.isArray(guardianChildren) && guardianChildren.length > 0) ? (
                                <SoftBox component="ul" display="flex" flexDirection="column" p={0} m={0}>
                                    {guardianChildren.map((childGuardian, index) => (
                                        <SoftBox key={index} component="li" display="flex"
                                                 justifyContent="space-between" alignItems="center" p={1} px={2}>
                                            <SoftBox mr={2} display="flex">
                                                <SoftBox mr={2}>
                                                    <SoftAvatar
                                                        src={childGuardian?.childResponse?.profilePic ? `data:image/png;base64,${childGuardian?.childResponse?.profilePic}` : undefined}
                                                        alt="child profile"
                                                        variant="rounded"
                                                        shadow="md"
                                                    />
                                                </SoftBox>
                                                <SoftBox mr={2} display="flex" flexDirection="column">
                                                    <SoftTypography variant="button" fontWeight="medium">
                                                        Name: {childGuardian?.childResponse?.firstName} {childGuardian?.childResponse?.lastName}
                                                    </SoftTypography>
                                                    <SoftTypography variant="caption" color="text">
                                                        ID: {childGuardian?.childResponse?.childId}
                                                    </SoftTypography>
                                                    <SoftTypography variant="caption" color="text">
                                                        Gender: {childGuardian?.childResponse?.gender}
                                                    </SoftTypography>
                                                    <SoftTypography variant="caption" color="text">
                                                        Relationship: {childGuardian.relationship}
                                                    </SoftTypography>
                                                    {childGuardian.dob && (
                                                        <SoftTypography variant="caption" color="text">
                                                            DOB: {new Date(childGuardian?.childResponse?.dob).toLocaleDateString()}
                                                        </SoftTypography>
                                                    )}
                                                </SoftBox>
                                            </SoftBox>
                                            <SoftBox display="flex" gap={1}>
                                                <SoftButton
                                                    variant="outlined"
                                                    color="primary"
                                                    size="small"
                                                    onClick={() => navigate(`/children/${childGuardian?.childResponse?.childId}`)}
                                                    startIcon={<Visibility/>}
                                                >
                                                    View
                                                </SoftButton>
                                                <SoftButton
                                                    variant="outlined"
                                                    color="error"
                                                    size="small"
                                                    onClick={() => handleDeleteChild(childGuardian?.childResponse)}
                                                    startIcon={<Delete/>}
                                                >
                                                    Remove
                                                </SoftButton>
                                            </SoftBox>
                                        </SoftBox>
                                    ))}
                                </SoftBox>
                            ) : (
                                <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight="100px">
                                    <SoftTypography variant="body2" color="text.secondary">
                                        No children assigned to this guardian
                                    </SoftTypography>
                                </SoftBox>
                            )}
                        </SoftBox>
                    </Card>
                </Grid>
            </Grid>

            <ChildManagementDialog
                open={childManagementOpen}
                onClose={() => setChildManagementOpen(false)}
                guardianId={guardian?.id}
                guardianChildren={guardianChildren}
                onUpdate={handleChildManagementUpdate}
            />

            <DeleteConfirmDialog
                open={deleteConfirmOpen}
                onClose={handleCancelDeleteChild}
                onDelete={handleConfirmDeleteChild}
                warningMessage={`Are you sure you want to remove ${childToDelete?.childResponse?.firstName} ${childToDelete?.childResponse?.lastName} from this guardian? This will only remove the relationship, not delete the child.`}
            />
        </SoftBox>
    );
}

GuardianDetail.propTypes = {
    guardian: PropTypes.objectOf(PropTypes.object).isRequired,
};

export default GuardianDetail;
